import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { Star, ArrowRight, TrendingUp, Clock, DollarSign, Users, Quote } from 'lucide-react'

export function SuccessStories() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "SourcedIn Success Stories",
    "description": "Real success stories from businesses using SourcedIn RFP marketplace",
    "mainEntity": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Organization",
          "name": "TechCorp Industries"
        },
        "reviewBody": "SourcedIn transformed our procurement process, reducing vendor sourcing time by 60% and improving supplier quality."
      }
    ]
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Success Stories - RFP Marketplace Case Studies | SourcedIn Customer Results"
        description="Discover how businesses use SourcedIn's RFP marketplace to find better vendors, reduce costs, and accelerate growth. Real case studies and customer testimonials."
        keywords="RFP marketplace success stories, procurement case studies, vendor matching results, B2B marketplace testimonials, customer success, business growth"
        canonical="https://sourcedin.ai/success-stories"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Real Success 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Stories</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Discover how businesses like yours are transforming their procurement processes 
            and achieving remarkable results with SourcedIn's RFP marketplace.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">95%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">60%</div>
              <div className="text-gray-600">Time Saved</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">$2M+</div>
              <div className="text-gray-600">Avg. Deal Size</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">10K+</div>
              <div className="text-gray-600">Happy Customers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Success Story */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <Card className="border-0 shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white p-12">
                <div className="mb-6">
                  <Quote className="h-12 w-12 opacity-50 mb-4" />
                  <blockquote className="text-2xl font-light leading-relaxed mb-6">
                    "SourcedIn completely revolutionized our procurement process. We went from 
                    spending months finding the right vendors to closing deals in just weeks."
                  </blockquote>
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <div className="font-semibold text-lg">Sarah Johnson</div>
                  <div className="opacity-90">Chief Procurement Officer, TechCorp Industries</div>
                </div>
              </div>
              <div className="p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  TechCorp Industries: 60% Faster Procurement
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">The Challenge</h3>
                    <p className="text-gray-600">
                      TechCorp struggled with lengthy procurement cycles, limited vendor visibility, 
                      and difficulty finding specialized technology partners for their complex projects.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">The Solution</h3>
                    <p className="text-gray-600">
                      By leveraging SourcedIn's AI-powered vendor matching and streamlined RFP process, 
                      TechCorp gained access to a global network of pre-qualified technology vendors.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">60%</div>
                      <div className="text-sm text-gray-600">Faster Process</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">40%</div>
                      <div className="text-sm text-gray-600">Cost Savings</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">200%</div>
                      <div className="text-sm text-gray-600">More Vendors</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Success Stories Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              More Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              See how businesses across industries are winning with SourcedIn
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Story 1 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <CardTitle className="text-xl">RetailMax Corporation</CardTitle>
                <CardDescription className="text-blue-600 font-medium">E-commerce & Retail</CardDescription>
              </CardHeader>
              <CardContent>
                <blockquote className="text-gray-700 italic mb-4">
                  "Found our perfect logistics partner in just 2 weeks. The quality of vendors 
                  on SourcedIn is exceptional."
                </blockquote>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">85%</div>
                    <div className="text-xs text-gray-600">Cost Reduction</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">3x</div>
                    <div className="text-xs text-gray-600">Faster Delivery</div>
                  </div>
                </div>
                <div className="mt-4 text-sm">
                  <div className="font-semibold">Mike Chen</div>
                  <div className="text-gray-600">Operations Director</div>
                </div>
              </CardContent>
            </Card>

            {/* Story 2 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <CardTitle className="text-xl">HealthFirst Medical</CardTitle>
                <CardDescription className="text-purple-600 font-medium">Healthcare</CardDescription>
              </CardHeader>
              <CardContent>
                <blockquote className="text-gray-700 italic mb-4">
                  "SourcedIn's compliance features and verified vendors gave us confidence 
                  in our medical equipment procurement."
                </blockquote>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">100%</div>
                    <div className="text-xs text-gray-600">Compliance Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">50%</div>
                    <div className="text-xs text-gray-600">Time Saved</div>
                  </div>
                </div>
                <div className="mt-4 text-sm">
                  <div className="font-semibold">Dr. Lisa Park</div>
                  <div className="text-gray-600">Procurement Manager</div>
                </div>
              </CardContent>
            </Card>

            {/* Story 3 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <CardTitle className="text-xl">GreenEnergy Solutions</CardTitle>
                <CardDescription className="text-green-600 font-medium">Renewable Energy</CardDescription>
              </CardHeader>
              <CardContent>
                <blockquote className="text-gray-700 italic mb-4">
                  "Connected with specialized solar panel manufacturers globally. 
                  Our project pipeline has never been stronger."
                </blockquote>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">300%</div>
                    <div className="text-xs text-gray-600">Pipeline Growth</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">25</div>
                    <div className="text-xs text-gray-600">New Partners</div>
                  </div>
                </div>
                <div className="mt-4 text-sm">
                  <div className="font-semibold">James Wilson</div>
                  <div className="text-gray-600">Business Development</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vendor Success Stories */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vendor Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              How suppliers are growing their business through our marketplace
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-lg text-gray-700 mb-6 italic">
                "SourcedIn has been a game-changer for our consulting firm. We've increased 
                our client base by 200% and the quality of projects has improved dramatically."
              </blockquote>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-semibold text-lg">David Rodriguez</div>
                  <div className="text-gray-600">CEO, Strategic Consulting Group</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">200%</div>
                  <div className="text-sm text-gray-600">Revenue Growth</div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-lg text-gray-700 mb-6 italic">
                "As a small design agency, SourcedIn leveled the playing field. We now compete 
                with larger firms and win enterprise contracts regularly."
              </blockquote>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-semibold text-lg">Emily Zhang</div>
                  <div className="text-gray-600">Founder, Creative Studio Pro</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">5x</div>
                  <div className="text-sm text-gray-600">Deal Size</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Results Overview */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Proven Results Across Industries
            </h2>
            <p className="text-xl opacity-90">
              Our customers consistently achieve outstanding outcomes
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-80" />
              <div className="text-3xl font-bold mb-2">70%</div>
              <div className="opacity-90">Faster Procurement</div>
            </div>
            <div className="text-center">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-80" />
              <div className="text-3xl font-bold mb-2">40%</div>
              <div className="opacity-90">Cost Savings</div>
            </div>
            <div className="text-center">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-80" />
              <div className="text-3xl font-bold mb-2">5x</div>
              <div className="opacity-90">More Vendors</div>
            </div>
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-80" />
              <div className="text-3xl font-bold mb-2">200%</div>
              <div className="opacity-90">Business Growth</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of businesses already transforming their procurement process with SourcedIn.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4">
                Start Your Success Story <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                Explore Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
